import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, Crop as CropIcon, RotateCw, ZoomIn } from 'lucide-react';

interface ImageEditorProps {
  imageUrl: string;
  onSave: (editedImage: string) => void;
  onClose: () => void;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export default function ImageEditor({ imageUrl, onSave, onClose }: ImageEditorProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [aspect, setAspect] = useState<number | undefined>(2.15); // Default to currency note aspect ratio

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CropArea,
    rotation = 0,
    flip = { horizontal: false, vertical: false }
  ): Promise<string> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Calculate bounding box of the rotated image
    const rotRad = (rotation * Math.PI) / 180;
    
    // Get dimensions based on rotation
    const rotatedWidth = Math.abs(pixelCrop.width * Math.cos(rotRad)) + Math.abs(pixelCrop.height * Math.sin(rotRad));
    const rotatedHeight = Math.abs(pixelCrop.width * Math.sin(rotRad)) + Math.abs(pixelCrop.height * Math.cos(rotRad));

    // Set canvas size to match the bounding box
    canvas.width = rotatedWidth;
    canvas.height = rotatedHeight;

    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Move to center of canvas
    ctx.translate(canvas.width / 2, canvas.height / 2);
    
    // Rotate around the center
    ctx.rotate(rotRad);
    
    // Scale if needed
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);

    // Draw the cropped image centered
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      -pixelCrop.width / 2,
      -pixelCrop.height / 2,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas.toDataURL('image/jpeg', 1.0);
  };

  const handleSave = async () => {
    if (croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(
          imageUrl,
          croppedAreaPixels,
          rotation
        );
        onSave(croppedImage);
      } catch (e) {
        console.error('Error cropping image:', e);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-start justify-center overflow-y-auto py-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-4xl mx-4 my-auto">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Edit Image</h3>
            <button
              onClick={() => setAspect(2.15)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                aspect === 2.15 ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Currency Note
            </button>
            <button
              onClick={() => setAspect(1)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                aspect === 1 ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Square
            </button>
            <button
              onClick={() => setAspect(undefined)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                !aspect ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Free
            </button>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="relative bg-black" style={{ height: '60vh' }}>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            objectFit="contain"
            showGrid={true}
            restrictPosition={false}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
                backgroundColor: '#000'
              },
              cropAreaStyle: {
                border: '2px solid #fff',
                color: 'rgba(255, 255, 255, 0.5)'
              },
              mediaStyle: {
                height: '100%',
                width: '100%',
                maxHeight: 'none',
                maxWidth: 'none',
                transform: 'none',
                objectFit: 'contain'
              }
            }}
          />
        </div>

        <div className="p-6 space-y-6 bg-gray-50">
          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <ZoomIn className="h-4 w-4" />
                Zoom
              </label>
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <RotateCw className="h-4 w-4" />
                Rotation
              </label>
              <input
                type="range"
                value={rotation}
                min={0}
                max={360}
                step={1}
                onChange={(e) => setRotation(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 font-medium"
            >
              <CropIcon className="h-5 w-5" />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}